// Google Identity Services (GIS): Google's own in-page "Continue with Google"
// button. On Chrome it uses the browser's native account sheet (FedCM), which is
// what makes sign-in feel like part of the page on phones instead of a separate
// tab. Web only; everything here fails soft so the existing popup sign-in
// (see googleAuth.ts) remains the fallback.
//
// Needs, outside this file:
//  - the site origin listed under "Authorized JavaScript origins" for the web
//    client id in the Google Cloud console, and
//  - the server's Content-Security-Policy to allow Google's script/frame/style
//    (see docs/NOTE-FOR-REFACTOR-GOOGLE-SIGNIN-CSP.md). Until then the script is
//    blocked, loadGoogleIdentity() rejects, and the modal keeps its own button.

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const LOAD_TIMEOUT_MS = 4000;

type CredentialResponse = { credential?: string };

type GoogleIdApi = {
    initialize: (config: {
        client_id: string;
        callback: (response: CredentialResponse) => void;
        use_fedcm_for_button?: boolean;
        use_fedcm_for_prompt?: boolean;
        itp_support?: boolean;
        auto_select?: boolean;
    }) => void;
    renderButton: (
        parent: HTMLElement,
        options: {
            type?: 'standard' | 'icon';
            theme?: 'outline' | 'filled_blue' | 'filled_black';
            size?: 'large' | 'medium' | 'small';
            text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
            shape?: 'rectangular' | 'pill';
            width?: number;
            logo_alignment?: 'left' | 'center';
        },
    ) => void;
};

type GoogleWindow = Window & { google?: { accounts?: { id?: GoogleIdApi } } };

let loading: Promise<GoogleIdApi> | null = null;

function readApi(): GoogleIdApi | null {
    return (window as GoogleWindow).google?.accounts?.id ?? null;
}

function loadGoogleIdentity(): Promise<GoogleIdApi> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.reject(new Error('gsi_unavailable'));
    }

    const existing = readApi();
    if (existing) return Promise.resolve(existing);
    if (loading) return loading;

    loading = new Promise<GoogleIdApi>((resolve, reject) => {
        const fail = (reason: string) => {
            loading = null;
            reject(new Error(reason));
        };
        const timer = setTimeout(() => fail('gsi_timeout'), LOAD_TIMEOUT_MS);

        try {
            const script = document.createElement('script');
            script.src = GSI_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                clearTimeout(timer);
                const api = readApi();
                if (api) resolve(api);
                else fail('gsi_missing');
            };
            script.onerror = () => {
                clearTimeout(timer);
                fail('gsi_blocked');
            };
            document.head.appendChild(script);
        } catch {
            // e.g. Trusted Types refusing the script URL.
            clearTimeout(timer);
            fail('gsi_blocked');
        }
    });

    return loading;
}

export { loadGoogleIdentity };
export type { CredentialResponse, GoogleIdApi };
